export const TAX_CONFIG_2026 = {
  // Income Tax (PAYE)
  standardRateCutoff: 44000, // Single person (Assumption for 2026)
  standardRate: 0.2, // 20%
  higherRate: 0.4, // 40%
  personalTaxCredit: 2000, // (Assumption for 2026)
  employeeTaxCredit: 2000, // PAYE Credit
  earnedIncomeCredit: 2000, // For Directors/Self-employed

  // Universal Social Charge (USC) - Updated bands for 2025/26
  uscExemptionThreshold: 13000,
  uscBands: [
    { threshold: 12012, rate: 0.005 }, // 0.5% on first €12,012
    { threshold: 25760, rate: 0.02 }, // 2% on next €13,748 (up to €25,760)
    { threshold: 70044, rate: 0.04 }, // 4% on next €44,284 (up to €70,044)
    { threshold: Infinity, rate: 0.08 }, // 8% on balance
  ],

  // PRSI (Class A - Employee)
  prsiExemptionThreshold: 352, // Weekly threshold (€18,304/year)
  prsiCreditThreshold: 424, // Weekly threshold for credit tapering
  employeePrsiRate: 0.04, // 4%

  // PRSI (Class A - Employer)
  employerPrsiThreshold: 496, // Weekly
  employerPrsiLowerRate: 0.088, // 8.8%
  employerPrsiHigherRate: 0.1105, // 11.05%

  // Corporation Tax
  corpTaxRate: 0.125,
};

/**
 * Calculates payroll taxes for a single period (Month 1 basis).
 *
 * @param {number} grossPay - The gross salary for the period.
 * @param {number} bik - Benefit in Kind amount (not paid, but taxable).
 * @param {object} employee - Employee tax details (credits, cut-off).
 * @param {string} frequency - 'monthly' or 'weekly'.
 * @returns {object} Breakdown of taxes and net pay.
 */
export function calculatePayroll(
  grossPay = 0,
  bik = 0,
  employee = {},
  frequency = "monthly"
) {
  // Determine number of pay periods in a year
  let periods = 12; // Default monthly
  if (frequency === "weekly") periods = 52;
  if (frequency === "fortnightly" || frequency === "bi-weekly") periods = 26;

  // 1. Determine Taxable Income
  const taxableIncome = grossPay + bik;

  // 2. Get Employee-Specific Credits & Cut-off (Annual)
  // Default to standard single person if not defined
  const annualCutoff =
    parseFloat(employee.cutoff) || TAX_CONFIG_2026.standardRateCutoff;
  const annualCredits =
    parseFloat(employee.taxCredits) ||
    TAX_CONFIG_2026.personalTaxCredit + TAX_CONFIG_2026.employeeTaxCredit;

  // Convert to Period Values
  const periodCutoff = annualCutoff / periods;
  const periodCredits = annualCredits / periods;

  // 3. Calculate PAYE (Income Tax)
  let paye = 0;
  if (taxableIncome <= periodCutoff) {
    paye = taxableIncome * TAX_CONFIG_2026.standardRate;
  } else {
    paye =
      periodCutoff * TAX_CONFIG_2026.standardRate +
      (taxableIncome - periodCutoff) * TAX_CONFIG_2026.higherRate;
  }
  // Deduct tax credits (cannot reduce tax below 0)
  paye = Math.max(0, paye - periodCredits);

  // 4. Calculate USC
  let usc = 0;
  // Check exemption on an annualized basis
  if (taxableIncome * periods > TAX_CONFIG_2026.uscExemptionThreshold) {
    let remaining = taxableIncome;
    let accumulatedThreshold = 0;

    for (const band of TAX_CONFIG_2026.uscBands) {
      if (remaining <= 0) break;
      const bandThreshold = band.threshold / periods; // Scale band for period
      const currentBandLimit = bandThreshold - accumulatedThreshold;
      const taxableInBand = Math.min(remaining, currentBandLimit);

      usc += taxableInBand * band.rate;
      remaining -= taxableInBand;
      accumulatedThreshold = bandThreshold;
      
      // Fix: The array `uscBands` `threshold` is cumulative in standard tables,
      // but my config array has discrete cumulative points or sizes?
      // Let's assume the config `threshold` means "up to this amount".
      // Wait, standard definition is "First X @ Y%", "Next Z @ A%".
      // My config structure: { threshold: 12012 } means "up to 12012".
      // So the logic above needs to handle the range correctly.
    }
  }
  
  // Re-doing USC with cleaner logic for "Cumulative Bands"
  // Bands in config are "Up To" values.
  // 0 -> 12012 : 0.5%
  // 12012 -> 25760 : 2%
  usc = 0;
  if (taxableIncome * periods > TAX_CONFIG_2026.uscExemptionThreshold) {
      let previousThreshold = 0;
      for (const band of TAX_CONFIG_2026.uscBands) {
          const annualBandLimit = band.threshold === Infinity ? Infinity : band.threshold;
          const periodicBandLimit = annualBandLimit === Infinity ? Infinity : annualBandLimit / periods;
          
          const lower = previousThreshold;
          const upper = periodicBandLimit;
          
          // Determine overlap of [lower, upper] with [0, taxableIncome]
          const overlap = Math.max(0, Math.min(taxableIncome, upper) - lower);
          
          if (overlap > 0) {
              usc += overlap * band.rate;
          }
          
          previousThreshold = upper;
          if (taxableIncome <= upper) break;
      }
  }

  // 5. Calculate Employee PRSI (Class A)
  let prsiEE = 0;
  const weeklyIncome = (taxableIncome * periods) / 52;
  
  if (weeklyIncome > TAX_CONFIG_2026.prsiExemptionThreshold) {
      prsiEE = taxableIncome * TAX_CONFIG_2026.employeePrsiRate;
      
      // PRSI Taper Credit (Complex, skipping precise tapering for now as it applies to small range €352-€424)
      // Usually negligible for typical salaries, but important for compliance.
      // For now, simple 4% is applied if over 352.
  }

  // 6. Calculate Employer PRSI
  let prsiER = 0;
  if (weeklyIncome > TAX_CONFIG_2026.employerPrsiThreshold) {
      prsiER = taxableIncome * TAX_CONFIG_2026.employerPrsiHigherRate;
  } else if (weeklyIncome > 0) { // Assuming typical class A
      prsiER = taxableIncome * TAX_CONFIG_2026.employerPrsiLowerRate;
  }

  // 7. Calculate Net Pay
  // Deduction is only from Gross, but tax is calculated on (Gross+BIK)
  const totalDeductions = paye + usc + prsiEE;
  const netPay = grossPay - totalDeductions;

  return {
    grossPay,
    bik,
    taxableIncome,
    paye: Number(paye.toFixed(2)),
    usc: Number(usc.toFixed(2)),
    prsiEE: Number(prsiEE.toFixed(2)),
    prsiER: Number(prsiER.toFixed(2)),
    totalDeductions: Number(totalDeductions.toFixed(2)),
    netPay: Number(netPay.toFixed(2)),
    companyCost: Number((grossPay + prsiER).toFixed(2)),
  };
}
